import { create } from "zustand";
import { supabase } from "./supabase";
import Swal from "sweetalert2";

const useEmployeesStore = create((set, get) => ({
  employees: [],
  loading: true,
  file: null,
  name: "",
  password: "",
  position: "",
  editingRow: null,
  rowData: {},

  setFile: (file) => set({ file }),

  setName: (name) => set({ name }),
  setPassword: (password) => set({ password }),
  setPosition: (position) => set({ position }),

  // Fetch employees
  fetchEmployees: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      set({ employees: data });
    } catch (error) {
      Swal.fire(
        "Error",
        "Failed to fetch employees. Check your internet connection.",
        "error"
      );
    } finally {
      set({ loading: false });
    }
  },

  // Add a new employee
  handleAddEmployee: async () => {
    const { name, position, password, file } = get();
    let publicUrl = "";
    const user = JSON.parse(localStorage.getItem("employee"));
    const userId = user.id;

    if (!name.trim() || !position.trim() || !password.trim()) {
      Swal.fire(
        "Error",
        "Name, Position and Password cannot be empty.",
        "error"
      );
      return;
    }

    if (file) {
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // 1️⃣ Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("profile_images")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // 2️⃣ Get public URL
      const { data } = supabase.storage
        .from("profile_images")
        .getPublicUrl(filePath);

      publicUrl = data.publicUrl;
    }

    try {
      const { data, error } = await supabase
        .from("employees")
        .insert([{ name, position, image: publicUrl || "", password }])
        .select();
      if (error) throw error;

      Swal.fire("Success", "Employee added successfully!", "success");
      set((state) => ({
        employees: [...state.employees, ...data],
        name: "",
        position: "",
        password: "",
      }));
    } catch (error) {
      Swal.fire("Error", "Failed to add employee.", "error");
    }
  },

  uploadProfilePicture: async (file, userId) => {
    const { fetchEmployees } = get();

    set({ loading: true });
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // 1️⃣ Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("profile_images")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // 2️⃣ Get public URL
      const { data } = supabase.storage
        .from("profile_images")
        .getPublicUrl(filePath);
      const publicUrl = data.publicUrl;

      console.log("publicUrl>>>>>", publicUrl);

      // 3️⃣ Update user record in DB

      const { data: updatedData, error } = await supabase
        .from("employees")
        .update({ image: publicUrl })
        .eq("id", userId)
        .select();

      if (error) throw error;

      set({ profile: { image: updatedData[0].image }, loading: false });

      Swal.fire("Success", "Profile picture uploaded successfully!", "success");

      fetchEmployees();
    } catch (err) {
      console.error("Upload error:", err);
      Swal.fire("Error", err.message, "error");
      set({ loading: false });
    }
  },

  // Start editing an employee
  handleEditStart: (id, row) => {
    set({ editingRow: id, rowData: { ...row } });
  },

  // Edit employee role
  handleEditRole: async (employee) => {
    const { value: role } = await Swal.fire({
      title: `Update role of ${employee.name}`,
      input: "select",
      inputOptions: {
        admin: "Admin",
        waiter: "Waiter",
        bartender: "Bartender",
        chef: "Chef",
      },
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      inputPlaceholder: `${employee.role}`,
      confirmButtonText: "Update",
      showLoaderOnConfirm: true,
      inputValidator: (value) => {
        if (!value) {
          return "Please select a role";
        }
      },
    });

    if (role) {
      try {
        const { error } = await supabase
          .from("employees")
          .update({ role })
          .eq("id", employee.id);
        if (error) throw error;

        get().fetchEmployees();
        Swal.fire("Success", "Employee role updated successfully!", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to update employee role.", "error");
      }
    }
  },

  // Update user status
  handleUpdateStatus: async (employee) => {
    if (employee.status === "active") {
      Swal.fire({
        title: `Are you sure you want to deactivate ${employee.name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, deactivate!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { error } = await supabase
            .from("employees")
            .update({ status: "inactive" })
            .eq("id", employee.id);
          if (error) throw error;

          get().fetchEmployees();

          Swal.fire({
            title: "Deactivated!",
            text: `${employee.name} has been deactivated.`,
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      });
    }

    if (employee.status === "inactive") {
      const { error } = await supabase
        .from("employees")
        .update({ status: "active" })
        .eq("id", employee.id);
      if (error) throw error;

      get().fetchEmployees();

      Swal.fire({
        title: "Activated!",
        text: `${employee.name} has been activated.`,
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  },

  // Handle changes during editing
  handleEditChange: (field, value) => {
    set((state) => ({ rowData: { ...state.rowData, [field]: value } }));
  },

  // Stop editing
  handleEditStop: () => {
    set({ editingRow: null, rowData: {} });
  },

  // Save edited employee
  handleSave: async (id) => {
    const { rowData } = get();
    try {
      const { error } = await supabase
        .from("employees")
        .update({
          name: rowData.name,
          status: rowData.status,
          image: rowData.image || "",
          password: rowData.password || "",
        })
        .eq("id", id);

      if (error) throw error;

      set((state) => ({
        employees: state.employees.map((row) =>
          row.id === id ? { ...row, ...rowData } : row
        ),
        editingRow: null,
        rowData: {},
      }));
      Swal.fire("Success", "Employee updated successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to update employee.", "error");
    }
  },

  // Delete an employee
  handleDelete: async (employee) => {
    Swal.fire({
      title: `Are you sure you want to delete ${employee.name}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { error } = await supabase
            .from("employees")
            .delete()
            .eq("id", employee.id);
          if (error) throw error;

          get().fetchEmployees();
          set((state) => ({
            employees: state.employees.filter((row) => row.id !== employee.id),
          }));
          Swal.fire({
            title: "Deleted!",
            text: "Employee has been deleted.",
            icon: "success",
          });
        } catch (error) {
          Swal.fire("Error", "Failed to delete employee.", "error");
        }
      }
    });
  },
}));

export default useEmployeesStore;
